import {
  AppId,
  AppSettings,
  SecurityPlus,
} from 'types/shared/documents/restaurants';

// Security Plus rights hold signatures (PIN initials) and right groups. A
// right protects only while at least one holder can still enter a PIN:
// usePinAuth opens a product without PIN for an empty list.
export type RightsChanges = Record<string, string[] | null>;

type ProtectionErrorCode = 'SECURITY_PLUS_INACTIVE' | 'NO_VALID_SIGNATURES';

const PROTECTION_ERROR_MESSAGES: Record<ProtectionErrorCode, string> = {
  SECURITY_PLUS_INACTIVE:
    'Security Plus ist nicht aktiv. Der PIN-Schutz kann nur mit einem aktiven Security Plus geändert werden.',
  NO_VALID_SIGNATURES:
    'Wähle mindestens eine vorhandene Signatur. Ohne Signatur öffnet sich das Produkt ohne PIN.',
};

export class ProtectionError extends Error {
  constructor(public code: ProtectionErrorCode, public right?: string) {
    super(PROTECTION_ERROR_MESSAGES[code]);
    this.name = 'ProtectionError';
  }
}

export const securityPlusUsable = (
  apps: AppId[],
  securityPlus: AppSettings<SecurityPlus> | null | undefined,
) =>
  apps.includes('security_plus') &&
  securityPlus?.active === true &&
  !!securityPlus.settings?.signatures &&
  typeof securityPlus.settings.signatures === 'object';

export function validHolders(
  settings: SecurityPlus | null | undefined,
  holders: unknown,
): string[] {
  if (!settings || !Array.isArray(holders)) return [];
  const signatures = settings.signatures ?? {};
  const isSignature = (id: string) => !!signatures[id];
  const isUsableGroup = (id: string) =>
    !!settings.rightGroups
      ?.find((group) => group.id === id)
      ?.signatures?.some(isSignature);
  return Array.from(
    new Set(
      holders.filter(
        (id): id is string =>
          typeof id === 'string' && (isSignature(id) || isUsableGroup(id)),
      ),
    ),
  );
}

export const currentHolders = (
  settings: SecurityPlus | null | undefined,
  right: string,
) => validHolders(settings, settings?.rights?.[right]);

const sameHolders = (a: unknown, b: string[]) =>
  Array.isArray(a) &&
  a.length === b.length &&
  b.every((holder) => a.includes(holder));

/** The rights paths to write; checked against the freshly read document. */
export function rightUpdates(
  securityPlus: AppSettings<SecurityPlus> | null | undefined,
  apps: AppId[],
  changes: RightsChanges,
): RightsChanges {
  const rights = Object.keys(changes);
  if (!rights.length) return {};
  if (!securityPlusUsable(apps, securityPlus)) {
    throw new ProtectionError('SECURITY_PLUS_INACTIVE');
  }
  const settings = securityPlus!.settings;

  const updates: RightsChanges = {};
  for (const right of rights) {
    const current = settings.rights?.[right] ?? null;
    const wanted = changes[right];
    if (wanted === null) {
      if (current !== null) updates[right] = null;
      continue;
    }
    const holders = validHolders(settings, wanted);
    if (!holders.length) throw new ProtectionError('NO_VALID_SIGNATURES', right);
    if (!sameHolders(current, holders)) updates[right] = holders;
  }
  return updates;
}
